import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useGame } from "@/context/GameContext";
import { adminPromos, adminUnlock } from "@/lib/payments";
import { Sound } from "@/lib/sound";
import { KeyRound, Plus, Trash2, Ticket, Lock, Unlock } from "lucide-react";
import { toast } from "sonner";

const AdminPanel = ({ open, onClose, code }) => {
  const { state, setUnlocked, relock } = useGame();
  const [promos, setPromos] = useState(null);
  const [newCode, setNewCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { setPromos(await adminPromos.list(code)); } catch (e) { setPromos([]); toast.error("Couldn't load promo codes"); }
  };
  useEffect(() => { if (open) load(); }, [open]); // eslint-disable-line

  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); Sound.click(); if (ok) toast.success(ok); await load(); }
    catch (e) { Sound.error(); toast.error(e?.response?.data?.detail || "Something went wrong"); }
    setBusy(false);
  };

  const toggleUnlock = async () => {
    if (state.unlocked) { relock(); Sound.whoosh(); toast.success("Locked again — only Dog Walker is free."); return; }
    const res = await adminUnlock(code);
    Sound.fanfare(); setUnlocked("admin", res); toast.success("Admin unlock: all businesses open!");
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg rounded-3xl border-4 border-slate-700 bg-white p-6" data-testid="admin-panel" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2"><KeyRound className="w-5 h-5" /> Admin</DialogTitle>
        </DialogHeader>

        <Button onClick={toggleUnlock} data-testid="admin-toggle-unlock-btn" className={`w-full font-heading font-black rounded-2xl btn-3d border-2 py-5 ${state.unlocked ? "bg-slate-800 hover:bg-slate-900 border-slate-900 text-white" : "bg-emerald-500 hover:bg-emerald-600 border-emerald-600 text-white"}`}>
          {state.unlocked ? <><Lock className="w-4 h-4 mr-1" /> Lock businesses again (test paywall)</> : <><Unlock className="w-4 h-4 mr-1" /> Unlock all 4 on this device</>}
        </Button>

        <div className="mt-4">
          <div className="text-xs font-black uppercase tracking-widest text-amber-600 mb-2 flex items-center gap-1"><Ticket className="w-3.5 h-3.5" /> Promo codes · unlimited uses</div>
          <div className="flex gap-2 mb-3">
            <Input value={newCode} onChange={(e) => setNewCode(e.target.value.toUpperCase())} placeholder="NEWCODE2026" className="rounded-xl border-2 border-slate-300 font-black tracking-widest uppercase" data-testid="admin-promo-input" onKeyDown={(e) => e.key === "Enter" && newCode.trim() && act(() => adminPromos.add(code, newCode).then(() => setNewCode("")), "Promo code added")} />
            <Button disabled={busy || !newCode.trim()} onClick={() => act(() => adminPromos.add(code, newCode).then(() => setNewCode("")), "Promo code added")} data-testid="admin-promo-add-btn" className="bg-amber-400 hover:bg-amber-500 text-slate-900 font-heading font-black rounded-xl border-2 border-amber-500">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          <div className="space-y-1.5 max-h-64 overflow-y-auto" data-testid="admin-promo-list">
            {promos === null && <div className="text-sm font-bold text-slate-500">Loading…</div>}
            {promos && promos.length === 0 && <div className="text-sm font-bold text-slate-500">No promo codes yet.</div>}
            {promos?.map((p) => (
              <div key={p.code} className={`flex items-center gap-2 rounded-xl border-2 px-3 py-2 ${p.active ? "bg-amber-50 border-amber-200" : "bg-slate-50 border-slate-200 opacity-70"}`} data-testid={`admin-promo-${p.code}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-black text-slate-900 tracking-widest truncate">{p.code}</div>
                  <div className="text-[10px] font-bold text-slate-500">{p.uses} {p.uses === 1 ? "use" : "uses"} · {p.active ? "active" : "paused"}</div>
                </div>
                <button disabled={busy} onClick={() => act(() => adminPromos.toggle(code, p.code, !p.active), p.active ? "Code paused" : "Code active")} data-testid={`admin-promo-toggle-${p.code}`} className="text-xs font-black uppercase tracking-wider text-sky-600 hover:text-sky-800">
                  {p.active ? "Pause" : "Activate"}
                </button>
                <button disabled={busy} onClick={() => window.confirm(`Delete ${p.code}? Anyone who used it will be locked again.`) && act(() => adminPromos.remove(code, p.code), "Code deleted")} data-testid={`admin-promo-delete-${p.code}`} className="text-slate-400 hover:text-rose-500">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-slate-500 font-semibold mt-2">Pausing or deleting a code locks every device that used it.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AdminPanel;
